import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import { Box, IconButton, Tooltip } from "@mui/material";
import React from "react";
import { ScormExportFileDeliveryStatus } from "services/KnowfullyAdminClient";
import { ExportedScormFileRow, Translate } from "./types";

type ActionsCellProps = {
  value: ExportedScormFileRow;
  t: Translate;
  isUploadable: (id: string) => boolean;
  isAwaitingAnUnreportedAttempt: (id: string) => boolean;
  onDownload: (row: ExportedScormFileRow) => void;
  onUpload: (row: ExportedScormFileRow) => void;
};

/**
 * Which sentence the Upload icon carries. Ordered, not a lookup: more than one of these can be true of the
 * same row and the earlier one is the one the operator needs.
 */
const uploadTooltipIdFor = (
  value: ExportedScormFileRow,
  uploadable: boolean,
  isAwaitingAnUnreportedAttempt: (id: string) => boolean
) => {
  if (value.status === ScormExportFileDeliveryStatus.Uploaded) {
    return "exportedScormFilesAlreadyUploadedTooltip";
  }

  if (!uploadable) {
    return "exportedScormFilesAlreadyQueuedTooltip";
  }

  // Ahead of the Failed wording on purpose: a row this screen already queued needs to say the previous
  // attempt has not reported, which is what separates a deliberate second push from one made in the belief
  // that nothing happened. "Retry this upload" would say the opposite.
  if (isAwaitingAnUnreportedAttempt(value.id)) {
    return "exportedScormFilesUnreportedAttemptTooltip";
  }

  if (value.status === ScormExportFileDeliveryStatus.Failed) {
    return "exportedScormFilesRetryUploadTooltip";
  }

  return "exportedScormFilesUploadTooltip";
};

/**
 * The Actions column: download this file, or push it to its organization's destination.
 *
 * ⛔ Upload never fires straight from this icon - it hands the row up so the page can confirm first. One
 * click here would otherwise reach a customer's live destination.
 */
export const ActionsCell = ({
  value,
  t,
  isUploadable,
  isAwaitingAnUnreportedAttempt,
  onDownload,
  onUpload,
}: ActionsCellProps) => {
  const uploadable = isUploadable(value.id);
  const uploadTooltipId = uploadTooltipIdFor(
    value,
    uploadable,
    isAwaitingAnUnreportedAttempt
  );

  return (
    <Box display="flex" justifyContent="flex-end">
      <Tooltip title={t({ id: "exportedScormFilesDownloadTooltip" })}>
        <IconButton size="small" onClick={() => onDownload(value)}>
          <FileDownloadIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title={t({ id: uploadTooltipId })}>
        {/* The span is what lets a disabled button still carry a tooltip. */}
        <span>
          <IconButton
            size="small"
            disabled={!uploadable}
            onClick={() => onUpload(value)}
          >
            <CloudUploadIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </Box>
  );
};

export { uploadTooltipIdFor };
